import { useTranslation } from 'react-i18next'
import { useArtists } from "@/hooks/useArtists"
import { getUniqueArtists } from "@/lib/tattooData"
import { useMemo, useRef, useState, useEffect } from "react"
import { Link } from "react-router-dom"
import type { PortfolioItem } from "@/types/models"
import { useAuthState } from "@/hooks/useAuth"
import { getUserVotesForWeek } from "@/lib/voteData"
import { formatArtistDisplayName } from "@/lib/utils"

interface FotwLeaderboardProps {
  items: PortfolioItem[]
  weekIndex: number
}

export function FotwLeaderboard({ items, weekIndex }: FotwLeaderboardProps) {
  const { t } = useTranslation()
  const { user } = useAuthState()
  const [hasVoted, setHasVoted] = useState(false)
  
  console.log('FotwLeaderboard render:', { user: user?.uid, hasVoted, weekIndex })
  
  // Check if user has voted this week
  useEffect(() => {
    console.log('useEffect triggered:', { user: user?.uid, weekIndex })
    const checkUserVotes = async () => {
      if (user) {
        console.log('About to call getUserVotesForWeek:', { userId: user.uid, weekIndex })
        const votes = await getUserVotesForWeek(user.uid, weekIndex)
        console.log('User votes check:', { userId: user.uid, weekIndex, votes, hasVoted: votes.length > 0 })
        setHasVoted(votes.length > 0)
      } else {
        console.log('No user, setting hasVoted to false')
        setHasVoted(false)
      }
    }
    checkUserVotes()
  }, [user, weekIndex, user?.uid, items]) // Added items as dependency to refresh when vote counts change
  
  // Also check votes when the component becomes visible (tab switching)
  useEffect(() => {
    const checkUserVotes = async () => {
      if (user) {
        const votes = await getUserVotesForWeek(user.uid, weekIndex)
        console.log('Visibility check - User votes:', { userId: user.uid, weekIndex, votes, hasVoted: votes.length > 0 })
        setHasVoted(votes.length > 0)
      }
    }
    
    // Run the check immediately when this component mounts/becomes visible
    checkUserVotes()
    
    // Also add a visibility change listener
    const handleVisibilityChange = () => {
      if (!document.hidden && user) {
        checkUserVotes()
      }
    }
    
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [user, weekIndex])
  
  const uniqueArtistIds = useMemo(() => getUniqueArtists(items), [items])
  const { artists } = useArtists(uniqueArtistIds)
  
  const top5Items = items.slice(0, 5)
  const [first, second, third, fourth, fifth] = top5Items
  
  const firstPlaceRef = useRef<HTMLAnchorElement>(null)
  const firstPlaceImageRef = useRef<HTMLImageElement>(null)
  const [imagesLoaded, setImagesLoaded] = useState(false)
  const [heights, setHeights] = useState({
    first: "auto",
    second: "auto",
    third: "auto"
  })

  useEffect(() => {
    const updateHeights = () => {
      if (firstPlaceRef.current && imagesLoaded) {
        const firstHeight = firstPlaceRef.current.offsetHeight
        if (firstHeight > 0) {
          setHeights({
            first: `${firstHeight}px`,
            second: `${firstHeight * 0.8}px`, // 80% of first place height
            third: `${firstHeight * 0.7}px`, // 70% of first place height
          })
        }
      }
    }

    updateHeights()
    // Add resize listener to handle window size changes
    window.addEventListener('resize', updateHeights)
    return () => window.removeEventListener('resize', updateHeights)
  }, [first, imagesLoaded]) // Recalculate when first place item changes or images load

  const handleFirstImageLoad = () => {
    setImagesLoaded(true)
  }

  // Reset images loaded state when first item changes
  useEffect(() => {
    setImagesLoaded(false)
  }, [first?.id])
  
  const getPositionStyles = () => {
    return "rounded-3xl shadow-lg hover:shadow-xl transition-shadow duration-200"
  }
  
  const getPositionBadge = (position: number) => {
    const badgeStyles = {
      1: "text-white", // Gold
      2: "text-white", // Silver
      3: "text-white", // Bronze
    }
    const backgroundStyles = {
      1: "bg-[#FFD700]", // Gold
      2: "bg-[#C0C0C0]", // Silver
      3: "bg-[#CD7F32]", // Bronze
    }
    
    return (
      <div className={`absolute -top-3 -left-3 w-14 h-14 rounded-full flex items-center justify-center font-bold text-lg border-[3px] border-white z-10 ${backgroundStyles[position as keyof typeof backgroundStyles] || ""} ${badgeStyles[position as keyof typeof badgeStyles] || ""}`}>
        {position}
      </div>
    )
  }
  
  const getArtistForItem = (item: PortfolioItem) => {
    return artists.find(artist => artist.id === item.artistId)
  }
  
  const getVoteCount = (item: PortfolioItem) => {
    // Use timedVotes if available, otherwise fall back to votes
    return item.timedVotes?.length || item.votes?.length || 0
  }
  
  const getCravesText = (count: number) => {
    if (user && hasVoted) {
      // Only show clear counts if user is logged in AND has voted
      if (count === 0) return t('fotw.leaderboard.noCraves')
      return t('fotw.leaderboard.craves', { count })
    } else {
      // Blur for everyone else (not logged in OR hasn't voted)
      return (
        <span>
          <span className="select-none" style={{ filter: 'blur(3px)' }}>{count}</span> 🙏
        </span>
      )
    }
  }
  
  if (items.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-lg font-medium text-foreground mb-2">
          {t('fotw.noResults.title')}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('fotw.noResults.description')}
        </p>
      </div>
    )
  }

  // Shared artist info component for top 3
  const ArtistInfo = ({ item, className = "" }: { item: PortfolioItem; className?: string }) => {
    const artist = getArtistForItem(item)
    const displayName = formatArtistDisplayName(artist) || artist?.displayName || "Artist"
    
    return (
      <div className={`mt-2 text-center w-full ${className}`}>
        <div className="flex items-center justify-center gap-2 mb-1 px-1">
          {artist?.avatarUrl && (
            <img
              src={artist.avatarUrl}
              alt={displayName}
              className="w-6 h-6 flex-shrink-0 rounded-full"
            />
          )}
          <span className="text-sm font-medium truncate">
            {displayName}
          </span>
        </div>
        <div className="inline-block bg-primary text-white text-xs px-2 py-1 rounded-full">
          {getCravesText(getVoteCount(item))}
        </div>
      </div>
    )
  }
  
  return (
    <div className="space-y-6">
      {(!user || !hasVoted) && (
        <div className="text-center text-sm text-muted-foreground mb-4">
          {t('fotw.leaderboard.voteToSee')}
        </div>
      )}
      
      {/* Top 3 Podium */}
      {top5Items.length >= 3 && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4 items-end pt-5">
          {/* 2nd Place */}
          {second && (
            <div className="flex flex-col items-center">
              <Link to={`/fotw/${second.id}`} className={`relative ${getPositionStyles()} w-full bg-white group`} style={{ height: heights.second }}>
                <img
                  src={second.compressedImageUrl || second.imageUrl}
                  alt={second.caption || "Flash design"}
                  className="w-full h-full object-contain rounded-3xl"
                />
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-black/20 transition-colors duration-200 rounded-3xl" />
                {getPositionBadge(2)}
              </Link>
              <ArtistInfo item={second} />
            </div>
          )}
          
          {/* 1st Place */}
          {first && (
            <div className="flex flex-col items-center">
              <Link to={`/fotw/${first.id}`} ref={firstPlaceRef} className={`relative ${getPositionStyles()} w-full bg-white group`} style={{ height: heights.first }}>
                <img
                  ref={firstPlaceImageRef}
                  src={first.compressedImageUrl || first.imageUrl}
                  alt={first.caption || "Flash design"}
                  className="w-full h-full object-contain rounded-3xl"
                  onLoad={handleFirstImageLoad}
                />
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-black/20 transition-colors duration-200 rounded-3xl" />
                {getPositionBadge(1)}
              </Link>
              <ArtistInfo item={first} />
            </div>
          )}
          
          {/* 3rd Place */}
          {third && (
            <div className="flex flex-col items-center">
              <Link to={`/fotw/${third.id}`} className={`relative ${getPositionStyles()} w-full bg-white group`} style={{ height: heights.third }}>
                <img
                  src={third.compressedImageUrl || third.imageUrl}
                  alt={third.caption || "Flash design"}
                  className="w-full h-full object-contain rounded-3xl"
                />
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-black/20 transition-colors duration-200 rounded-3xl" />
                {getPositionBadge(3)}
              </Link>
              <ArtistInfo item={third} />
            </div>
          )}
        </div>
      )}
      
      {/* Positions 4 & 5 */}
      {(fourth || fifth) && (
        <div className="space-y-3">
          {fourth && (
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-center w-8 h-8 bg-foreground text-background rounded-full font-bold text-sm flex-shrink-0">
                4
              </div>
              <Link to={`/fotw/${fourth.id}`} className="w-22 h-22 bg-white rounded-lg overflow-hidden flex-shrink-0 group relative shadow-lg hover:shadow-xl transition-shadow duration-200">
                <img
                  src={fourth.imageUrl}
                  alt={fourth.caption || "Flash design"}
                  className="w-full h-full object-contain"
                />
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-black/20 transition-colors duration-200" />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {formatArtistDisplayName(getArtistForItem(fourth)) || getArtistForItem(fourth)?.displayName || "Artist"}
                </div>
                <div className="inline-block text-sm text-white bg-primary px-2 py-0.5 rounded-full mt-1">
                  {getCravesText(getVoteCount(fourth))}
                </div>
              </div>
            </div>
          )}
          
          {fifth && (
            <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
              <div className="flex items-center justify-center w-8 h-8 bg-foreground text-background rounded-full font-bold text-sm flex-shrink-0">
                5
              </div>
              <Link to={`/fotw/${fifth.id}`} className="w-22 h-22 bg-white rounded-lg overflow-hidden flex-shrink-0 group relative shadow-lg hover:shadow-xl transition-shadow duration-200">
                <img
                  src={fifth.imageUrl}
                  alt={fifth.caption || "Flash design"}
                  className="w-full h-full object-contain"
                />
                <div className="absolute inset-0 bg-primary/5 group-hover:bg-black/20 transition-colors duration-200" />
              </Link>
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate">
                  {formatArtistDisplayName(getArtistForItem(fifth)) || getArtistForItem(fifth)?.displayName || "Artist"}
                </div>
                <div className="inline-block text-sm text-white bg-primary px-2 py-0.5 rounded-full mt-1">
                  {getCravesText(getVoteCount(fifth))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
} 